import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
// Switch removed: PRUNE mode eliminado (sincronización es siempre solo crear/actualizar)
import { toast } from "sonner";
import {
  CloudDownload, RefreshCw, Database, FolderArchive, ShieldAlert,
  Image, FileText, AlertTriangle, CheckCircle2, HardDrive,
  Wifi, WifiOff, Save, ExternalLink,
} from "lucide-react";

// La URL del preview de Emergent puede cambiar. NO la dejamos hardcodeada.
// Se carga desde /admin/content-sync/preview-config (system_config).
const PREVIEW_URL_PLACEHOLDER = "https://TU-PREVIEW.preview.emergentagent.com";

export default function ContentSyncPage() {
  const { axiosAuth } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [form, setForm] = useState({
    source_url: "",
    source_email: "",
    source_password: "",
  });
  const [lastReport, setLastReport] = useState(null);

  // Preview restore (fotos y textos)
  const [uploadsStatus, setUploadsStatus] = useState(null);
  const [previewRestoring, setPreviewRestoring] = useState(false);
  const [previewReport, setPreviewReport] = useState(null);
  const [previewForm, setPreviewForm] = useState({
    source_url: "",
    source_email: "",
    source_password: "",
    include_promotions: false,
    include_form_config: false,
    include_email_templates: false,
    include_wa_templates: false,
    include_uploads: true,
  });

  // URL config persistente + test conexión
  const [savedUrl, setSavedUrl] = useState({ source_url: "", updated_at: null, updated_by: null });
  const [savingUrl, setSavingUrl] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);  // {ok, http_status, message, latency_ms}

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosAuth().get("/admin/content-sync/summary");
      setSummary(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error cargando resumen");
    } finally {
      setLoading(false);
    }
  }, [axiosAuth]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const fetchUploadsStatus = useCallback(async () => {
    try {
      const res = await axiosAuth().get("/admin/content-sync/uploads-status");
      setUploadsStatus(res.data);
    } catch (err) {
      // 404 si versión vieja, lo ignoramos
      setUploadsStatus({ persistent: false, error: err.response?.data?.detail });
    }
  }, [axiosAuth]);

  useEffect(() => { fetchUploadsStatus(); }, [fetchUploadsStatus]);

  // Cargar URL persistente
  const fetchSavedUrl = useCallback(async () => {
    try {
      const res = await axiosAuth().get("/admin/content-sync/preview-config");
      setSavedUrl(res.data);
      if (res.data.source_url) {
        setPreviewForm(f => ({ ...f, source_url: res.data.source_url }));
        setForm(f => ({ ...f, source_url: res.data.source_url }));
      }
    } catch (err) {
      // No bloqueante
    }
  }, [axiosAuth]);

  useEffect(() => { fetchSavedUrl(); }, [fetchSavedUrl]);

  const saveSourceUrl = async () => {
    const url = (previewForm.source_url || "").trim();
    if (!url) { toast.error("Escribe una URL primero"); return; }
    setSavingUrl(true);
    try {
      const res = await axiosAuth().put("/admin/content-sync/preview-config", { source_url: url });
      setSavedUrl({
        source_url: res.data.source_url,
        updated_at: new Date().toISOString(),
        updated_by: "tú",
      });
      toast.success("URL del preview guardada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar la URL");
    } finally {
      setSavingUrl(false);
    }
  };

  const testConnection = async () => {
    const url = (previewForm.source_url || "").trim();
    if (!url) { toast.error("Escribe una URL primero"); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axiosAuth().post("/admin/content-sync/test-source", { source_url: url });
      setTestResult(res.data);
      if (res.data.ok) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Error probando conexión";
      setTestResult({ ok: false, message: msg, http_status: null });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const runPreviewRestore = async () => {
    if (!previewForm.source_url || !previewForm.source_email || !previewForm.source_password) {
      toast.error("Completa URL, email y contraseña del origen (preview)");
      return;
    }

    // VALIDACIÓN OBLIGATORIA: health-check antes de cualquier llamada que toque beta.
    // Si /api/health no responde 200, bloqueamos la restauración aquí mismo.
    setTesting(true);
    let healthOk = false;
    try {
      const res = await axiosAuth().post("/admin/content-sync/test-source", { source_url: previewForm.source_url });
      setTestResult(res.data);
      healthOk = !!res.data.ok;
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Error en health-check";
      setTestResult({ ok: false, message: msg, http_status: null });
    } finally {
      setTesting(false);
    }
    if (!healthOk) {
      toast.error("Health-check del preview FALLÓ. Operación bloqueada para no tocar beta.");
      return;
    }

    if (previewForm.include_uploads && uploadsStatus && !uploadsStatus.persistent) {
      if (!window.confirm(
        "El directorio /app/uploads NO está marcado como persistente en este entorno.\n" +
        "Si continúas, las imágenes podrían perderse al reiniciar el contenedor.\n\n" +
        "El servidor BLOQUEARÁ la operación. ¿Continuar igualmente para ver el aviso?"
      )) return;
    }
    if (!window.confirm(
      "Restaurar fotos y textos desde preview\n\n" +
      "• Crea/actualiza contenido, NUNCA borra.\n" +
      "• No toca usuarios, empleados, vehículos, citas, bonos ni roles.\n" +
      "• Operación segura para BETA.\n\n" +
      "¿Continuar?"
    )) return;

    setPreviewRestoring(true);
    setPreviewReport(null);
    try {
      const res = await axiosAuth().post(
        "/admin/content-sync/preview-restore",
        previewForm,
        { timeout: 600000 },
      );
      setPreviewReport(res.data);
      toast.success("Fotos y textos restaurados (sin borrar nada)");
      fetchSummary();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Error en preview-restore";
      toast.error(detail);
      setPreviewReport({ error: detail });
    } finally {
      setPreviewRestoring(false);
    }
  };

  const downloadExport = async () => {
    try {
      const res = await axiosAuth().get("/admin/content-sync/export", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/gzip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url;
      a.download = `ascauto_content_${stamp}.tar.gz`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Tarball descargado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error en la descarga");
    }
  };

  const pullFromSource = async () => {
    if (!form.source_url || !form.source_email || !form.source_password) {
      toast.error("Completa URL, email y contraseña del origen");
      return;
    }
    setPulling(true);
    setLastReport(null);
    try {
      const res = await axiosAuth().post("/admin/content-sync/pull", form, { timeout: 180000 });
      setLastReport(res.data);
      toast.success("Sincronización completada (solo crea/actualiza, nunca borra)");
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Error en la sincronización");
    } finally {
      setPulling(false);
    }
  };

  const totalDocs = summary
    ? Object.values(summary.collections).reduce((a, b) => a + b, 0)
    : 0;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="content-sync-page">
      <div>
        <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase">
          Sincronización de Contenido
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Exporta o importa contenido público (site_content, hero slider, centros, promociones, galerías, uploads…) entre entornos.
        </p>
      </div>

      {/* Resumen */}
      <Card className="border-slate-200 rounded-sm" data-testid="sync-summary-card">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
            <Database className="w-4 h-4 text-red-600" /> Contenido disponible en este entorno
          </CardTitle>
          <Button variant="outline" size="sm" onClick={fetchSummary} disabled={loading}
            className="rounded-sm text-xs uppercase h-8" data-testid="refresh-summary-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Actualizar
          </Button>
        </CardHeader>
        <CardContent>
          {summary ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                {Object.entries(summary.collections).map(([k, v]) => (
                  <div key={k} className="flex justify-between items-center px-3 py-2 bg-slate-50 border border-slate-200 rounded-sm">
                    <span className="text-xs text-slate-600">{k}</span>
                    <span className="text-sm font-semibold text-slate-900" data-testid={`sync-count-${k}`}>{v}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-4 text-xs text-slate-500 border-t border-slate-100 pt-3">
                <span><strong className="text-slate-800">{totalDocs}</strong> documentos</span>
                <span className="flex items-center gap-1">
                  <FolderArchive className="w-3.5 h-3.5" />
                  <strong className="text-slate-800">{summary.uploads_files}</strong> archivos en uploads
                </span>
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-400">Cargando...</p>
          )}
        </CardContent>
      </Card>

      {/* Restaurar fotos y textos desde preview */}
      <Card className="border-red-200 rounded-sm bg-red-50/30" data-testid="preview-restore-card">
        <CardHeader className="pb-3">
          <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
            <Image className="w-4 h-4 text-red-600" /> Restaurar fotos y textos desde preview
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-slate-600 leading-relaxed">
            Botón seguro para sincronizar el contenido visual y los textos públicos desde el preview de Emergent
            hacia este entorno (beta). <strong>Nunca toca producción.</strong> Por defecto copia <code className="bg-white border px-1 rounded text-[10px]">site_content</code>,{" "}
            <code className="bg-white border px-1 rounded text-[10px]">hero_slides</code>,{" "}
            <code className="bg-white border px-1 rounded text-[10px]">branding</code> y los archivos de <code className="bg-white border px-1 rounded text-[10px]">uploads/</code>.
          </p>

          {/* Persistencia uploads */}
          {uploadsStatus && (
            <div
              className={`p-3 border rounded-sm flex items-start gap-2 ${
                uploadsStatus.persistent
                  ? "border-green-200 bg-green-50"
                  : "border-amber-200 bg-amber-50"
              }`}
              data-testid="uploads-persistence-notice"
            >
              {uploadsStatus.persistent ? (
                <CheckCircle2 className="w-4 h-4 text-green-700 mt-0.5 flex-shrink-0" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
              )}
              <div className="text-[11px] leading-relaxed">
                <strong className="block uppercase tracking-wider text-[10px] mb-0.5 flex items-center gap-1">
                  <HardDrive className="w-3 h-3" /> Persistencia de uploads
                </strong>
                {uploadsStatus.persistent ? (
                  <span className="text-green-900">
                    <code className="bg-white border px-1 rounded text-[10px]">{uploadsStatus.uploads_dir}</code> marcado como volumen persistente.
                  </span>
                ) : (
                  <span className="text-amber-900">
                    <code className="bg-white border px-1 rounded text-[10px]">{uploadsStatus.uploads_dir || "/app/uploads"}</code> NO está marcado como persistente.
                    El servidor bloqueará la copia de archivos. Define <code className="bg-white border px-1 rounded text-[10px]">UPLOADS_PERSISTENT=true</code> y verifica el volumen en <code className="bg-white border px-1 rounded text-[10px]">docker-compose.staging.yml</code>.
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">URL origen (preview)</Label>
              <div className="flex flex-wrap gap-2">
                <Input value={previewForm.source_url}
                  onChange={e => setPreviewForm({ ...previewForm, source_url: e.target.value })}
                  placeholder={PREVIEW_URL_PLACEHOLDER}
                  className="rounded-sm h-9 border-slate-200 font-mono text-xs flex-1 min-w-[280px]"
                  data-testid="preview-source-url" />
                <Button onClick={testConnection} disabled={testing || !previewForm.source_url}
                  variant="outline"
                  className="rounded-sm h-9 text-[11px] uppercase tracking-wider border-slate-300"
                  data-testid="test-source-btn">
                  {testing
                    ? <><RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> Probando...</>
                    : <><Wifi className="w-3.5 h-3.5 mr-1" /> Probar conexión</>}
                </Button>
                <Button onClick={saveSourceUrl} disabled={savingUrl || !previewForm.source_url}
                  variant="outline"
                  className="rounded-sm h-9 text-[11px] uppercase tracking-wider border-slate-300"
                  data-testid="save-source-btn">
                  <Save className={`w-3.5 h-3.5 mr-1 ${savingUrl ? "animate-pulse" : ""}`} /> Guardar URL
                </Button>
              </div>
              <p className="text-[11px] text-slate-500 leading-relaxed" data-testid="preview-url-help">
                La URL del preview de Emergent puede cambiar. Copia aquí la URL real del preview activo.
                Debe responder correctamente a <code className="bg-slate-100 px-1 rounded text-[10px]">/api/health</code>.
              </p>
              {savedUrl.source_url && (
                <p className="text-[10px] text-slate-500" data-testid="saved-url-indicator">
                  Última URL guardada: <code className="bg-slate-50 border px-1 rounded text-[10px]">{savedUrl.source_url}</code>
                  {savedUrl.updated_by && <> · por {savedUrl.updated_by}</>}
                </p>
              )}
              {testResult && (
                <div
                  className={`flex items-start gap-2 px-2.5 py-1.5 border rounded-sm text-[11px] leading-relaxed ${
                    testResult.ok
                      ? "border-green-200 bg-green-50 text-green-900"
                      : "border-red-200 bg-red-50 text-red-900"
                  }`}
                  data-testid="test-result"
                >
                  {testResult.ok
                    ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    : <WifiOff className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />}
                  <div className="flex-1">
                    <strong className="block text-[10px] uppercase tracking-wider mb-0.5">
                      {testResult.ok ? "Conexión OK" : "Sin conexión"}
                    </strong>
                    <span>{testResult.message}</span>
                    {testResult.http_status != null && (
                      <span className="ml-2 opacity-75">[HTTP {testResult.http_status} · {testResult.latency_ms}ms]</span>
                    )}
                  </div>
                  {previewForm.source_url && (
                    <a href={`${previewForm.source_url.replace(/\/+$/, "")}/api/health`}
                      target="_blank" rel="noreferrer"
                      className="text-[10px] underline opacity-80 hover:opacity-100 flex items-center gap-0.5">
                      Abrir <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Email admin (origen)</Label>
              <Input value={previewForm.source_email}
                onChange={e => setPreviewForm({ ...previewForm, source_email: e.target.value })}
                placeholder="jr.ascaso@ascauto.com" autoComplete="username"
                className="rounded-sm h-9 border-slate-200" data-testid="preview-source-email" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Contraseña (origen)</Label>
              <Input type="password" value={previewForm.source_password}
                onChange={e => setPreviewForm({ ...previewForm, source_password: e.target.value })}
                autoComplete="current-password"
                className="rounded-sm h-9 border-slate-200" data-testid="preview-source-password" />
            </div>
          </div>

          {/* Colecciones a copiar */}
          <div className="border border-slate-200 bg-white rounded-sm p-3 space-y-2" data-testid="preview-collections">
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-1">Qué se copia</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs">
              <div className="flex items-center gap-2 px-2 py-1 bg-green-50 border border-green-200 rounded-sm">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-700" />
                <span><code className="text-[10px]">site_content</code> · siempre</span>
              </div>
              <div className="flex items-center gap-2 px-2 py-1 bg-green-50 border border-green-200 rounded-sm">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-700" />
                <span><code className="text-[10px]">hero_slides</code> · siempre</span>
              </div>
              <div className="flex items-center gap-2 px-2 py-1 bg-green-50 border border-green-200 rounded-sm">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-700" />
                <span><code className="text-[10px]">branding</code> · siempre</span>
              </div>
              <label className="flex items-center gap-2 px-2 py-1 border border-slate-200 rounded-sm cursor-pointer hover:bg-slate-50">
                <Checkbox checked={previewForm.include_uploads}
                  onCheckedChange={v => setPreviewForm({ ...previewForm, include_uploads: !!v })}
                  data-testid="cb-include-uploads" />
                <span><code className="text-[10px]">uploads/</code> · archivos</span>
              </label>
              <label className="flex items-center gap-2 px-2 py-1 border border-slate-200 rounded-sm cursor-pointer hover:bg-slate-50">
                <Checkbox checked={previewForm.include_promotions}
                  onCheckedChange={v => setPreviewForm({ ...previewForm, include_promotions: !!v })}
                  data-testid="cb-include-promotions" />
                <span><code className="text-[10px]">promotions</code> · opcional</span>
              </label>
              <label className="flex items-center gap-2 px-2 py-1 border border-slate-200 rounded-sm cursor-pointer hover:bg-slate-50">
                <Checkbox checked={previewForm.include_form_config}
                  onCheckedChange={v => setPreviewForm({ ...previewForm, include_form_config: !!v })}
                  data-testid="cb-include-form-config" />
                <span><code className="text-[10px]">form_config</code> · opcional</span>
              </label>
              <label className="flex items-center gap-2 px-2 py-1 border border-slate-200 rounded-sm cursor-pointer hover:bg-slate-50">
                <Checkbox checked={previewForm.include_email_templates}
                  onCheckedChange={v => setPreviewForm({ ...previewForm, include_email_templates: !!v })}
                  data-testid="cb-include-email-templates" />
                <span><code className="text-[10px]">email_templates</code> · opcional</span>
              </label>
              <label className="flex items-center gap-2 px-2 py-1 border border-slate-200 rounded-sm cursor-pointer hover:bg-slate-50">
                <Checkbox checked={previewForm.include_wa_templates}
                  onCheckedChange={v => setPreviewForm({ ...previewForm, include_wa_templates: !!v })}
                  data-testid="cb-include-wa-templates" />
                <span><code className="text-[10px]">wa_templates</code> · opcional</span>
              </label>
            </div>
          </div>

          {/* Avisos */}
          <div className="bg-green-50 border border-green-200 rounded-sm p-3 text-[11px] leading-relaxed text-green-900" data-testid="preview-restore-notice">
            <strong className="block uppercase tracking-wider text-[10px] mb-1 flex items-center gap-1">
              <ShieldAlert className="w-3 h-3" /> Reglas que aplica este botón
            </strong>
            <ul className="list-disc pl-4 space-y-0.5">
              <li>Solo crea o actualiza. <strong>Nunca borra</strong> ni archivos ni documentos.</li>
              <li>Nunca toca usuarios, roles, empleados, citas, vehículos, bonos, auditoría ni contraseñas.</li>
              <li>Hace health-check del origen. Si falla, aborta sin cambios.</li>
              <li>Solo se ejecuta contra BETA. Bloqueado para producción.</li>
            </ul>
          </div>

          <Button onClick={runPreviewRestore} disabled={previewRestoring}
            className="bg-red-600 hover:bg-red-700 text-white rounded-sm text-xs uppercase tracking-wider h-10 px-5 font-bold"
            data-testid="preview-restore-btn">
            <Image className={`w-4 h-4 mr-2 ${previewRestoring ? "animate-pulse" : ""}`} />
            {previewRestoring ? "Restaurando..." : "Restaurar fotos y textos desde preview"}
          </Button>

          {previewReport && (
            <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-sm" data-testid="preview-restore-report">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-700 mb-2">Resultado</p>
              <div className="text-[11px] font-mono text-slate-700 max-h-72 overflow-auto whitespace-pre-wrap">
                {JSON.stringify(previewReport, null, 2)}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Export */}
      <Card className="border-slate-200 rounded-sm" data-testid="sync-export-card">
        <CardHeader className="pb-3">
          <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
            <CloudDownload className="w-4 h-4 text-red-600" /> Descargar tarball (export)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-slate-500">
            Descarga un <code className="text-[11px] bg-slate-100 px-1">.tar.gz</code> con todo el contenido público y los archivos de <code className="text-[11px] bg-slate-100 px-1">uploads/</code>. Luego súbelo al VPS y ejecuta <code className="text-[11px] bg-slate-100 px-1">scripts/import_content.py</code>.
          </p>
          <Button onClick={downloadExport} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-xs uppercase tracking-wider h-9"
            data-testid="download-export-btn">
            <CloudDownload className="w-4 h-4 mr-2" /> Descargar tarball
          </Button>
        </CardContent>
      </Card>

      {/* Pull */}
      <Card className="border-slate-200 rounded-sm" data-testid="sync-pull-card">
        <CardHeader className="pb-3">
          <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-red-600" /> Tirar contenido desde otro entorno (pull)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-slate-500">
            Ideal para usar en el VPS: descarga automáticamente desde el entorno origen (p. ej. preview de desarrollo) y aplica el tarball aquí mismo.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">URL origen</Label>
              <Input value={form.source_url} onChange={e => setForm({ ...form, source_url: e.target.value })}
                placeholder={PREVIEW_URL_PLACEHOLDER}
                className="rounded-sm h-9 border-slate-200" data-testid="pull-source-url" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Email admin (origen)</Label>
              <Input value={form.source_email} onChange={e => setForm({ ...form, source_email: e.target.value })}
                placeholder="admin@taller.com" autoComplete="username"
                className="rounded-sm h-9 border-slate-200" data-testid="pull-source-email" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Contraseña</Label>
              <Input type="password" value={form.source_password} onChange={e => setForm({ ...form, source_password: e.target.value })}
                autoComplete="current-password"
                className="rounded-sm h-9 border-slate-200" data-testid="pull-source-password" />
            </div>
          </div>

          <div className="flex items-start gap-2 p-3 border border-green-200 bg-green-50 rounded-sm" data-testid="no-prune-notice">
            <ShieldAlert className="w-4 h-4 text-green-700 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-green-900">Modo seguro · sin borrado</p>
              <p className="text-[11px] text-green-800">
                Esta sincronización solo CREA o ACTUALIZA contenido. Nunca borra documentos del destino.
                Si algo existe aquí y no está en el origen, se conserva. Las colecciones operativas
                (usuarios, empleados, citas, bonos, etc.) están excluidas por seguridad.
              </p>
            </div>
          </div>

          <Button onClick={pullFromSource} disabled={pulling}
            className="bg-red-600 hover:bg-red-700 text-white rounded-sm text-xs uppercase tracking-wider h-9"
            data-testid="pull-execute-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${pulling ? "animate-spin" : ""}`} />
            {pulling ? "Sincronizando..." : "Sincronizar ahora"}
          </Button>

          {lastReport && (
            <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-sm" data-testid="pull-report">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-700 mb-2">Resultado</p>
              <div className="text-[11px] font-mono text-slate-600 max-h-64 overflow-auto whitespace-pre-wrap">
                {JSON.stringify(lastReport, null, 2)}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cron tip */}
      <Card className="border-slate-200 rounded-sm bg-slate-50/60" data-testid="sync-cron-card">
        <CardHeader className="pb-3">
          <CardTitle className="heading-industrial text-base uppercase">Programar automáticamente (cron)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-xs text-slate-600">
          <p>Para que el VPS se sincronice cada noche a las 03:30 desde la preview de desarrollo:</p>
          <pre className="p-3 bg-slate-900 text-slate-100 rounded-sm text-[11px] overflow-x-auto leading-relaxed">{`# /etc/cron.d/ascauto-content-sync
30 3 * * * root /usr/bin/curl -s -X POST \\
  https://api-beta.ascauto.com/api/admin/content-sync/pull \\
  -H "Content-Type: application/json" \\
  -u "admin@taller.com:LA_CONTRASENA_ADMIN_DEL_VPS" \\
  -d '{
    "source_url":"https://TU-PREVIEW.preview.emergentagent.com",
    "source_email":"admin@taller.com",
    "source_password":"admin"
  }' > /var/log/ascauto-sync.log 2>&1`}</pre>
          <p className="text-[11px] text-slate-500 mt-2">
            Reemplaza las credenciales y la URL de preview. Usa un usuario admin dedicado con contraseña fuerte para cron.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
